import { useState, useMemo } from 'react'
import type { AdminResultItem, ActiveFilter } from '../types/admin'

interface QuestionInfo {
  id: string
  label: string
}

interface RespondentListProps {
  results: AdminResultItem[]
  questions: QuestionInfo[]
  activeFilters: ActiveFilter[]
}

function matchesFilters(result: AdminResultItem, filters: ActiveFilter[]): boolean {
  if (filters.length === 0) return true
  const data = result.data || {}
  return filters.every((f) => {
    const answer = data[f.questionId]
    if (Array.isArray(answer)) return answer.some((v) => String(v) === f.value)
    return String(answer ?? '') === f.value
  })
}

export function RespondentList({ results, questions, activeFilters }: RespondentListProps) {
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    let list = results.filter((r) => matchesFilters(r, activeFilters))
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((r) =>
        (r.respondentName || '').toLowerCase().includes(q) ||
        (r.respondentEmail || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [results, activeFilters, search])

  const questionMap = useMemo(() => new Map(questions.map((q) => [q.id, q.label])), [questions])

  function formatAnswer(value: unknown): string {
    if (value === null || value === undefined) return '—'
    if (Array.isArray(value)) return value.join(', ')
    return String(value)
  }

  return (
    <div className="respondent-responses">
      <div className="respondent-search-bar">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="respondent-search-input"
        />
        <span className="respondent-count">{filtered.length} of {results.length}</span>
      </div>

      <ul className="respondent-response-list">
        {filtered.map((r) => {
          const isOpen = expandedId === r.id
          return (
            <li key={r.id} className={`respondent-response-item ${isOpen ? 'expanded' : ''}`}>
              <button
                className="respondent-response-header"
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                aria-expanded={isOpen}
              >
                <div className="respondent-response-name">
                  <strong>{r.respondentName || 'Anonymous'}</strong>
                  {r.respondentEmail && <span className="respondent-response-email">{r.respondentEmail}</span>}
                </div>
                <div className="respondent-response-meta">
                  {r.submittedAt && <span>{new Date(r.submittedAt).toLocaleDateString()}</span>}
                  <span className={`completion-badge ${r.completed ? 'completed' : 'incomplete'}`}>
                    {r.completed ? 'Completed' : 'Incomplete'}
                  </span>
                  <span className="expand-icon">{isOpen ? '\u25B2' : '\u25BC'}</span>
                </div>
              </button>

              <div className={`respondent-answers-panel ${isOpen ? 'open' : ''}`}>
                <div className="respondent-answers-inner">
                  {r.data && (
                    <div className="respondent-answers">
                      {Object.entries(r.data).map(([qId, value]) => (
                        <div key={qId} className="answer-row">
                          <span className="answer-question">{questionMap.get(qId) || qId}</span>
                          <span className="answer-value">{formatAnswer(value)}</span>
                        </div>
                      ))}
                      {Object.keys(r.data).length === 0 && (
                        <div className="answer-row answer-empty">No answers recorded</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          )
        })}
        {filtered.length === 0 && (
          <li className="respondent-response-item respondent-empty">
            No responses match the current filters.
          </li>
        )}
      </ul>
    </div>
  )
}
